import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Image as ImageIcon, Download, ArrowLeft, Shield, Zap, FileImage, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const CompressImagePage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [compressedUrl, setCompressedUrl] = useState(null);
  const [originalSize, setOriginalSize] = useState(0);
  const [compressedSize, setCompressedSize] = useState(0);
  const [quality, setQuality] = useState(80);
  const [isCompressing, setIsCompressing] = useState(false);
  const [showBookmarkBanner, setShowBookmarkBanner] = useState(true);

  React.useEffect(() => {
    const bookmarkDismissed = localStorage.getItem('compress-image-bookmark-dismissed');
    if (bookmarkDismissed === 'true') {
      setShowBookmarkBanner(false);
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setOriginalSize(file.size);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Reset compressed state
      setCompressedUrl(null);
      setCompressedSize(0);
    }
  };

  const handleCompress = async () => {
    if (!selectedFile) return;
    
    setIsCompressing(true);
    
    try {
      // Create canvas to compress image
      const img = new Image();
      img.src = previewUrl;
      
      await new Promise((resolve) => {
        img.onload = resolve;
      });
      
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      
      // Convert to blob with specified quality
      canvas.toBlob((blob) => {
        setCompressedSize(blob.size);
        const url = URL.createObjectURL(blob);
        setCompressedUrl(url);
        setIsCompressing(false);
      }, selectedFile.type, quality / 100);
      
    } catch (error) {
      console.error('Compression error:', error);
      setIsCompressing(false);
    }
  };

  const handleDownload = () => {
    if (!compressedUrl) return;
    
    // Extract filename without extension
    const fileName = selectedFile.name;
    const lastDotIndex = fileName.lastIndexOf('.');
    const nameWithoutExt = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
    const extension = lastDotIndex > 0 ? fileName.substring(lastDotIndex) : '';
    
    const link = document.createElement('a');
    link.href = compressedUrl;
    link.download = `${nameWithoutExt}_AllSmallTools_Compressed${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const compressionPercentage = originalSize && compressedSize 
    ? Math.round(((originalSize - compressedSize) / originalSize) * 100) 
    : 0;

  const handleDismissBookmarkBanner = () => {
    setShowBookmarkBanner(false);
    localStorage.setItem('compress-image-bookmark-dismissed', 'true');
  };

  return (
    <>
      <Helmet>
        <title>Compress Image Online — Reduce Image Size Fast | AllSmallTooLS</title>
        <meta name="description" content="Reduce image size without losing quality. Free image compressor for JPG, PNG, and WebP formats." />
        <meta name="keywords" content="compress image, image compressor, reduce image size, photo compression online, shrink jpg" />
        <link rel="canonical" href="https://allsmalltools.com/compress-image" />
      </Helmet>

      <div className="space-y-6">
      {/* Bookmark Banner */}
      {showBookmarkBanner && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-indigo-200 rounded-2xl p-4 sm:p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3 text-center sm:text-left">
              <div className="bg-indigo-100 p-3 rounded-full flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
              <div>
                <p className="text-base sm:text-lg font-semibold text-gray-900">
                  Bookmark this page for quick access to image compressor anytime
                </p>
                <p className="text-sm text-gray-600 hidden sm:block">
                  Press Ctrl+D (Windows/Linux) or Cmd+D (Mac) to bookmark
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const isMac = navigator.userAgent.toLowerCase().indexOf('mac') !== -1;
                  const shortcut = isMac ? 'Cmd+D' : 'Ctrl+D';
                  if (window.sidebar && window.sidebar.addPanel) {
                    window.sidebar.addPanel(document.title, window.location.href, '');
                  } else if (window.external && ('AddFavorite' in window.external)) {
                    window.external.AddFavorite(window.location.href, document.title);
                  } else {
                    alert(`To bookmark this page, press ${shortcut} on your keyboard.\n\nThis will save quick access to your image compressor for future use.`);
                  }
                }}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 flex items-center space-x-2 whitespace-nowrap"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                <span>Bookmark Now</span>
              </button>

              <button
                onClick={handleDismissBookmarkBanner}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 hover:text-gray-900 p-3 rounded-lg transition-all duration-200 flex-shrink-0 shadow-sm hover:shadow-md"
                aria-label="Dismiss bookmark banner"
                title="Don't show again"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
              <ImageIcon className="h-8 w-8 text-blue-500" />
              <span>Compress Image</span>
            </h1>
            <p className="text-gray-600">Reduce image file size while maintaining quality</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle>Upload Image</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                id="image-upload"
              />
              <label htmlFor="image-upload" className="cursor-pointer">
                <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-sm text-gray-600">
                  Click to select an image or drag and drop
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Supports JPG, PNG, WebP
                </p>
              </label>
            </div>

            {previewUrl && (
              <div className="space-y-4">
                <div>
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    className="max-w-full h-auto rounded-lg"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quality: {quality}%
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="100"
                    value={quality}
                    onChange={(e) => setQuality(Number(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Smaller size</span>
                    <span>Better quality</span>
                  </div>
                </div>

                <div className="text-sm text-gray-600">
                  <p>Original size: <span className="font-semibold">{formatFileSize(originalSize)}</span></p>
                </div>

                <Button
                  onClick={handleCompress}
                  disabled={isCompressing}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {isCompressing ? 'Compressing...' : 'Compress Image'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Result Section */}
        <Card>
          <CardHeader>
            <CardTitle>Compressed Result</CardTitle>
          </CardHeader>
          <CardContent>
            {compressedUrl ? (
              <div className="space-y-4">
                <div>
                  <img 
                    src={compressedUrl} 
                    alt="Compressed" 
                    className="max-w-full h-auto rounded-lg"
                  />
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Original:</span>
                    <span className="font-semibold">{formatFileSize(originalSize)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Compressed:</span>
                    <span className="font-semibold">{formatFileSize(compressedSize)}</span>
                  </div>
                  <div className="flex justify-between text-sm pt-2 border-t border-green-300">
                    <span className="text-gray-600">Reduction:</span>
                    <span className="font-semibold text-green-600">{compressionPercentage}%</span>
                  </div>
                </div>

                <Button
                  onClick={handleDownload}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Compressed Image
                </Button>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <ImageIcon className="h-16 w-16 mx-auto mb-4" />
                <p>Compressed image will appear here</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <ImageIcon className="h-6 w-6 text-blue-600" />
            <div className="text-sm text-blue-700">
              <strong>How it works:</strong> Upload an image, adjust the quality slider to your preference, and click compress. Lower quality = smaller file size. The compression happens in your browser - your images are never uploaded to a server.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Why Use Our Compress Image Tool Section */}
      <Card className="mt-12">
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Why Use Our Image Compressor?</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <FileImage className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Reduce File Size</h3>
              <p className="text-gray-600">
                Compress images to reduce file size significantly. Our image compressor optimizes JPG, PNG, and WebP files for web and email usage.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-green-100 p-3 rounded-lg">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Quality Control</h3>
              <p className="text-gray-600">
                Adjust compression quality with our slider. Balance between file size reduction and image quality. Preview before downloading compressed images.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Zap className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Browser-Based</h3>
              <p className="text-gray-600">
                All compression happens in your browser. Your images never leave your device. No upload required – completely private and secure image compression.
              </p>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-700">
              Need to compress images for faster website loading, email attachments, or storage? Our free image compressor makes it easy. Reduce image file size while maintaining acceptable quality. Perfect for optimizing web graphics. Try our compress image tool today!
            </p>
            <div className="mt-4 flex flex-wrap gap-4">
              <Link to="/resize-image" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Resize Images
              </Link>
              <Link to="/convert-image" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Convert Images
              </Link>
              <Link to="/remove-background" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Remove Background
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How to Compress Images Section */}
      <Card>
        <CardHeader>
          <h3 className="text-xl font-bold text-gray-900">How to Compress Images Online</h3>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4 text-gray-700">
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">1</span>
              <div>
                <strong>Upload Your Image:</strong> Click to select your image file or drag and drop. Our compressor supports JPG, PNG, and WebP formats.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">2</span>
              <div>
                <strong>Adjust Quality:</strong> Use the quality slider to control compression level. Lower quality means smaller file size. See original file size displayed.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">3</span>
              <div>
                <strong>Compress Image:</strong> Click "Compress Image" to process. View compression results showing file size reduction percentage.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">4</span>
              <div>
                <strong>Download Compressed File:</strong> Preview the compressed image and check the new file size. Download your optimized image instantly.
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Is image compression free?</h3>
                {openFaq === 0 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 0 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Yes! Our image compressor is completely free. Compress unlimited images without registration or fees. All processing happens in your browser.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">How much can I compress an image?</h3>
                {openFaq === 1 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 1 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Compression rates vary by image. Typically 50-80% reduction is achievable. Adjust the quality slider to balance file size vs quality. Preview results before downloading.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Does compression reduce image quality?</h3>
                {openFaq === 2 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 2 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Some quality loss occurs with compression, but it's usually minimal at 80% quality. You can control the trade-off between file size and quality using our slider.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Is my image data safe?</h3>
                {openFaq === 3 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 3 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Absolutely! All image compression happens in your browser. Your images are never uploaded to our servers. Complete privacy guaranteed.</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default CompressImagePage;
